/**
 * The composer's file handling that is not the attach call itself (#1732): telling a file drag from any other, taking
 * the files out of a drop or a paste, naming a clipboard image that arrives with no name, and refusing files over the
 * limits before any is uploaded.
 */

import { CHAT_FILE_LIMITS, type ChatFileLimits, chatFileLimitsSentence, formatChatFileSize } from '@zerohuman/shared'

/** The part of a drag's or a paste's DataTransfer this reads; a browser's real one is a superset of it. */
export type FileTransfer = {
    types?: ArrayLike<string> | readonly string[]
    files?: ArrayLike<File> | null
    items?: ArrayLike<{ kind: string; getAsFile: () => File | null }> | null
}

/** Whether a drag carries files, rather than text or a link: only such a drag shows the drop state. */
export function isFileDrag(transfer: FileTransfer | null | undefined): boolean {
    return Array.from(transfer?.types ?? []).includes('Files')
}

/**
 * The files a drop or a paste carries. `files` where the browser fills it; else the file items, which is where some
 * browsers put a pasted screenshot. A paste of text carries none.
 */
export function transferFiles(transfer: FileTransfer | null | undefined): File[] {
    const files = Array.from(transfer?.files ?? [])
    if (files.length > 0) {
        return files
    }
    return Array.from(transfer?.items ?? [])
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter((file): file is File => file !== null)
}

/** The extension a nameless clipboard file is given, from its type: the image kinds chat takes. */
const EXTENSION_BY_TYPE: ReadonlyMap<string, string> = new Map([
    ['image/png', 'png'],
    ['image/jpeg', 'jpg'],
    ['image/jpg', 'jpg'],
])

/**
 * A pasted file as it is attached: as it is when it has a name; a clipboard image with none (a screenshot) gets one,
 * `pasted-image-<time>.png` or `.jpg` from its type, so the server's judgement by extension takes it. A nameless file
 * of any other type is named without an extension, and so refused in the server's words like any unsupported file.
 */
export function namedForAttaching(file: File, now: Date = new Date()): File {
    if (file.name) {
        return file
    }
    const stamp = now.toISOString().replace(/\D/g, '').slice(0, 14)
    const extension = EXTENSION_BY_TYPE.get(file.type)
    const name = extension ? `pasted-image-${stamp}.${extension}` : `pasted-file-${stamp}`
    return new File([file], name, { type: file.type })
}

/**
 * Why files are refused before any is uploaded (#1732, review of #1888), or null to upload them: a file over the
 * per-file limit, or more files than one message carries counted with the `pending` ones already held for it. One
 * plain sentence naming what broke, ending with the shared limits, and nothing attached, as the server refuses a
 * whole request. The server judges the same limits again; this spares the browser encoding and sending what it would
 * refuse, and a body past what the attach route reads, which can be cut off before its refusal is read.
 */
export function refusedBeforeUpload(
    files: readonly File[],
    pending: number,
    limits: ChatFileLimits = CHAT_FILE_LIMITS,
): string | null {
    const reasons: string[] = []
    const count = pending + files.length
    if (count > limits.maxFilesPerMessage) {
        reasons.push(`This message would have ${count} files.`)
    }
    for (const file of files) {
        if (file.size > limits.maxFileBytes) {
            reasons.push(`${file.name} is larger than ${formatChatFileSize(limits.maxFileBytes)}.`)
        }
    }
    if (reasons.length === 0) {
        return null
    }
    return `${reasons.join(' ')} Nothing was attached: one message can carry ${chatFileLimitsSentence(limits)}.`
}
